import { useState, useCallback } from 'react';
import { ICommentsAndObservations } from 'types/global';
import { getOrderCommentsByOrderId } from 'services/orders';
import { submitComment } from 'views/LogisticServices/utils/general.utils';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';

/**
 * Hook que encapsula la lógica de comentarios de una orden.
 * Permite abrir/cerrar el modal, cargar comentarios y enviar nuevos comentarios.
 * @param orderId - ID de la orden para la cual se gestionan los comentarios
 * @param kamUserId - ID opcional del KAM para asociar al comentario
 * @returns Objeto con estados y handlers para gestionar los comentarios
 */
const useOrderComments = ({ orderId, kamUserId }: UseOrderCommentsParams): UseOrderCommentsReturn => {
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [orderComments, setOrderComments] = useState<ICommentsAndObservations[]>([]);

  const handleOpenOrCloseCommentsModal = useCallback(async () => {
    const willOpenModal = !showCommentsModal;
    setShowCommentsModal(willOpenModal);

    if (!willOpenModal || !orderId) return;

    setIsLoadingComments(true);
    try {
      const comments = await getOrderCommentsByOrderId(Number(orderId));
      setOrderComments(comments);
    } catch {
      setOrderComments([]);
      displayErrorNotification();
    } finally {
      setIsLoadingComments(false);
    }
  }, [showCommentsModal, orderId]);

  const handleCommentsSubmission = useCallback(
    async (comment: ICommentsAndObservations) => {
      if (!orderId) return;

      setIsLoadingComments(true);
      try {
        await submitComment({
          orderId,
          allowNewCommentsSubmission: true,
          handleLoading: setIsLoadingComments,
          comments: comment.comments,
          mentions: comment.mentions || [],
          commentImage: comment?.commentImage,
          ...(kamUserId && { kamUserId })
        });

        const updatedComments = await getOrderCommentsByOrderId(Number(orderId));
        setOrderComments(updatedComments);
      } catch {
        displayErrorNotification();
      } finally {
        setIsLoadingComments(false);
      }
    },
    [orderId, kamUserId]
  );

  return {
    showCommentsModal,
    isLoadingComments,
    orderComments,
    handleOpenOrCloseCommentsModal,
    handleCommentsSubmission
  };
};

interface UseOrderCommentsParams {
  orderId?: number;
  kamUserId?: number;
}

interface UseOrderCommentsReturn {
  showCommentsModal: boolean;
  isLoadingComments: boolean;
  orderComments: ICommentsAndObservations[];
  handleOpenOrCloseCommentsModal: () => Promise<void>;
  handleCommentsSubmission: (comment: ICommentsAndObservations) => Promise<void>;
}

export default useOrderComments;
